import React from 'react';
import { motion } from 'framer-motion';

function TermsOfService() {
  return (
    <div className="min-h-screen bg-white py-12 px-6">
      <div className="container mx-auto max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-5xl font-bold mb-4 text-gray-900">Terms of Service</h1>
          <p className="text-gray-600 mb-8">Last updated: January 3, 2025</p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                By accessing or using the AVALW Marketplace platform, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, do not use our platform.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">2. Platform Overview</h2>
              <p className="text-gray-700 leading-relaxed">
                AVALW Marketplace is an extension distribution platform for Cronos Browser. We provide:
              </p>
              <ul className="list-disc list-inside text-gray-700 mt-4 space-y-2">
                <li>A marketplace for discovering and installing browser extensions</li>
                <li>Developer tools for creating and publishing extensions</li>
                <li>Binary compilation service to protect extension source code</li>
                <li>Review and approval system for marketplace submissions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">3. User Accounts</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">3.1 Account Creation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    You must create an account to access certain features. You agree to:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                    <li>Provide accurate and complete information</li>
                    <li>Maintain the security of your account credentials</li>
                    <li>Notify us immediately of any unauthorized access</li>
                    <li>Be responsible for all activities under your account</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">3.2 Account Termination</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We reserve the right to suspend or terminate accounts that violate these terms or engage in fraudulent, abusive, or illegal activities.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">4. Developer Terms</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">4.1 Extension Submission</h3>
                  <p className="text-gray-700 leading-relaxed">
                    When submitting extensions, you warrant that:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                    <li>You own or have rights to the code and content</li>
                    <li>Your extension does not infringe on third-party rights</li>
                    <li>The extension complies with all applicable laws</li>
                    <li>You have disclosed all data collection and usage</li>
                    <li>The extension does not contain malware or malicious code</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">4.2 Code Compilation</h3>
                  <p className="text-gray-700 leading-relaxed">
                    By uploading source code, you grant us the right to compile your code into .avalw binary format. Your source code remains your intellectual property and is protected as outlined in our Privacy Policy.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">4.3 Review Process</h3>
                  <p className="text-gray-700 leading-relaxed">
                    All extensions undergo security and policy review before publication. Review typically takes 24-48 hours. We may reject extensions that:
                  </p>
                  <ul className="list-disc list-inside text-gray-700 mt-2 space-y-1">
                    <li>Contain security vulnerabilities</li>
                    <li>Violate user privacy</li>
                    <li>Infringe on intellectual property</li>
                    <li>Contain misleading descriptions</li>
                    <li>Engage in deceptive practices</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">4.4 Revenue Sharing</h3>
                  <p className="text-gray-700 leading-relaxed">
                    For paid extensions, we retain a platform fee of 30% of gross revenue. Payments are processed monthly for balances exceeding $100. Developers are responsible for applicable taxes.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">5. Prohibited Content and Conduct</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Extensions and user conduct must not:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Contain malware, spyware, or malicious code</li>
                <li>Collect user data without clear disclosure and consent</li>
                <li>Inject advertisements in deceptive ways</li>
                <li>Interfere with browser or system functionality</li>
                <li>Violate copyright, trademark, or other intellectual property rights</li>
                <li>Contain illegal content or facilitate illegal activities</li>
                <li>Impersonate others or misrepresent affiliation</li>
                <li>Engage in cryptocurrency mining without disclosure</li>
                <li>Manipulate reviews or ratings</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">6. Intellectual Property Rights</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">6.1 Platform Ownership</h3>
                  <p className="text-gray-700 leading-relaxed">
                    AVALW Marketplace, including its software, design, branding, and compilation technology, is owned by us and protected by intellectual property laws.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">6.2 Extension Ownership</h3>
                  <p className="text-gray-700 leading-relaxed">
                    You retain all rights to your extension source code. The compiled .avalw binary is a derivative work that you license to users through our platform.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">6.3 DMCA Compliance</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We respond to valid DMCA takedown notices. If you believe content infringes your copyright, contact us at dmca@avalw.com with required information.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">7. Disclaimers and Limitations</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">7.1 Service Availability</h3>
                  <p className="text-gray-700 leading-relaxed">
                    We provide the platform "as is" without warranties. We do not guarantee uninterrupted access or error-free operation.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">7.2 Extension Quality</h3>
                  <p className="text-gray-700 leading-relaxed">
                    While we review extensions, we do not guarantee their quality, safety, or functionality. Users install extensions at their own risk.
                  </p>
                </div>

                <div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900">7.3 Limitation of Liability</h3>
                  <p className="text-gray-700 leading-relaxed">
                    To the maximum extent permitted by law, we are not liable for indirect, incidental, consequential, or punitive damages arising from platform use.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">8. Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify and hold harmless AVALW Marketplace, its affiliates, and personnel from claims, damages, and expenses arising from your use of the platform, your extensions, or violation of these terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">9. Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed">
                Disputes will be resolved through binding arbitration in accordance with the rules of the American Arbitration Association. You waive the right to participate in class actions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">10. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We may modify these terms at any time. Material changes will be notified via email or platform notice. Continued use after changes constitutes acceptance of new terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">11. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These terms are governed by the laws of the United States, without regard to conflict of law provisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">12. Contact Information</h2>
              <p className="text-gray-700 leading-relaxed">
                For questions about these Terms of Service, contact us at:
              </p>
              <div className="mt-4 p-6 bg-gray-50 rounded-lg border-2 border-gray-200">
                <p className="text-gray-900 font-semibold">Email: legal@avalw.com</p>
                <p className="text-gray-700 mt-2">Support: support@avalw.com</p>
              </div>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

export default TermsOfService;
